import clsx from 'clsx';
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from './Button';
import { IconClose, IconMore } from './icons';

/**
 * Floating layers. Only these carry shadow.
 * - Dialog:  short, focused tasks (create, confirm). Max width by size.
 * - Popover: one small task done beside its anchor (pick days, tune a value).
 * - Menu:    anchored list of actions.
 * Record detail panels are *not* layers — they are part of the page layout
 * (see Panel) so the list stays visible and navigable.
 */

/**
 * Stacking tier for V2 overlays. Legacy layers top out at
 * SearchableSelect (100001), above the legacy Modal (100000) and sticky
 * headers/drawers (99999); a V2 dialog must paint above all of them while
 * the two systems coexist. Menus are positioned inside their own stacking
 * context and need no global tier.
 */
export const OVERLAY_Z = { dialog: 100010 } as const;

/** Open V2 layers, oldest first. Only the last one reacts to Escape. */
const layerStack: object[] = [];

/**
 * Registers an open layer so Escape closes it before anything beneath it
 * (a menu or listbox inside a dialog closes first; the dialog stays open).
 */
export function useEscape(open: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const token = {};
    layerStack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || layerStack[layerStack.length - 1] !== token) {
        return;
      }
      // Captured on document so the top layer consumes the key before any
      // page or legacy overlay listener sees it.
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const i = layerStack.indexOf(token);
      if (i !== -1) layerStack.splice(i, 1);
    };
  }, [open]);
}

/** Open dialogs, oldest first. Only the last one traps Tab. */
const dialogStack: HTMLElement[] = [];

export function useFocusTrap(open: boolean, ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!open || !ref.current) return;
    const root = ref.current;
    const prev = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(
        root.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ),
      );
    const first = focusables().find((el) => el.dataset.autofocus === 'true');
    (first ?? focusables()[0] ?? root).focus();
    dialogStack.push(root);
    // Listens on document, not the panel: if focus has fallen out of the
    // dialog (e.g. to <body> after a focused control was disabled), Tab
    // still brings it back instead of reaching the page behind.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || dialogStack[dialogStack.length - 1] !== root) {
        return;
      }
      const list = focusables();
      const active = document.activeElement;
      if (list.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const a = list[0];
      const z = list[list.length - 1];
      if (!active || !root.contains(active) || active === root) {
        e.preventDefault();
        (e.shiftKey ? z : a).focus();
      } else if (e.shiftKey && active === a) {
        e.preventDefault();
        z.focus();
      } else if (!e.shiftKey && active === z) {
        e.preventDefault();
        a.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const i = dialogStack.indexOf(root);
      if (i !== -1) dialogStack.splice(i, 1);
      // The dialog's DOM is already gone, so focus that was inside it has
      // fallen back to <body>. If focus is elsewhere (another dialog still
      // open above), leave it there.
      const active = document.activeElement;
      if (!active || active === document.body) prev?.focus?.();
    };
  }, [open, ref]);
}

/** Open dialogs holding the body scroll lock; the first saves, the last restores. */
let scrollLocks = 0;
let unlockedOverflow = '';

export function useScrollLock(open: boolean) {
  useEffect(() => {
    if (!open) return;
    if (scrollLocks === 0) {
      unlockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    scrollLocks += 1;
    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = unlockedOverflow;
    };
  }, [open]);
}

const noop = () => {};

export const Dialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * When false (e.g. while an irreversible action is in flight), Escape,
   * the backdrop and the Close button don't dismiss the dialog.
   */
  dismissible?: boolean;
}> = ({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  dismissible = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const dismiss = dismissible ? onClose : noop;
  useEscape(open, dismiss);
  useFocusTrap(open, ref);
  useScrollLock(open);
  if (!open) return null;
  return createPortal(
    <div
      className="av2 fixed inset-0 flex items-start justify-center overflow-y-auto bg-transparent px-4 py-[8vh]"
      style={{ zIndex: OVERLAY_Z.dialog }}
    >
      <div
        aria-hidden
        className="fixed inset-0 bg-v2-fg/30 dark:bg-black/60"
        onClick={dismiss}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={clsx(
          'v2-fade-in relative w-full rounded-v2-lg bg-v2-surface shadow-v2-dialog outline-none',
          size === 'sm' && 'max-w-[420px]',
          size === 'md' && 'max-w-[560px]',
          size === 'lg' && 'max-w-[720px]',
        )}
      >
        <header className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
          <div>
            <h2 id={titleId} className="text-v2-16 font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-v2-12 text-v2-fg-3">
                {description}
              </p>
            )}
          </div>
          <IconButton
            label="Close"
            onClick={dismiss}
            disabledReason={dismissible ? undefined : 'Wait for this to finish'}
            className="-mr-2 -mt-1"
          >
            <IconClose />
          </IconButton>
        </header>
        <div className="px-5 pb-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-v2-line px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};

/**
 * Confirmation for irreversible actions only (cancel-and-refund, delete,
 * revoke). Everything reversible happens in place with no confirmation.
 * One sentence of consequence, optional reason field, one danger button.
 */
export const ConfirmDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  /** One sentence: what happens and what can't be undone. */
  consequence: React.ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  children?: React.ReactNode;
}> = ({
  open,
  onClose,
  onConfirm,
  title,
  consequence,
  confirmLabel,
  tone = 'danger',
  busy,
  children,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    dismissible={!busy}
    title={title}
    size="sm"
    footer={
      <>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Keep as is
        </Button>
        <Button
          variant={tone}
          onClick={onConfirm}
          loading={busy}
          data-autofocus="true"
        >
          {confirmLabel}
        </Button>
      </>
    }
  >
    <p className="text-v2-13 leading-5 text-v2-fg-2">{consequence}</p>
    {children && <div className="mt-3">{children}</div>}
  </Dialog>
);

/** Phones get the bottom sheet; everything wider gets the anchored panel. */
const onPhone = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 639px)').matches;

/**
 * Anchored panel for one small task done in place: more than a Menu's list
 * of actions, less than a Dialog's step away from the page. Floats by its
 * anchor and flips above it when the viewport is short; on a phone it is a
 * bottom sheet over a scrim. Escape, an outside press and Close dismiss it;
 * focus stays inside while open and returns to the anchor after.
 */
export const Popover: React.FC<{
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  title: string;
  children: React.ReactNode;
  /** Right-aligned like Dialog's; lead with `mr-auto` for a status line. */
  footer?: React.ReactNode;
  width?: number;
}> = ({ open, onClose, anchorRef, title, children, footer, width = 320 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [phone, setPhone] = useState(onPhone);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEscape(open, onClose);
  useFocusTrap(open, ref);
  useScrollLock(open && phone);

  // Before paint, and again as the page scrolls or resizes under it. The
  // anchor can sit inside any scrolling ancestor, hence the capture flag.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const sheet = onPhone();
      setPhone(sheet);
      const box = anchorRef.current?.getBoundingClientRect();
      if (sheet || !box) return;
      const height = ref.current?.getBoundingClientRect().height ?? 0;
      const below = box.bottom + 6;
      const top =
        below + height <= window.innerHeight - 12
          ? below
          : Math.max(
              12,
              Math.min(box.top - 6 - height, window.innerHeight - 12 - height),
            );
      setPos({
        left: Math.max(
          12,
          Math.min(box.right - width, window.innerWidth - width - 12),
        ),
        top,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      className="av2 pointer-events-none fixed inset-0"
      style={{ zIndex: OVERLAY_Z.dialog }}
    >
      {phone && (
        <div
          aria-hidden
          className="pointer-events-auto fixed inset-0 bg-v2-fg/30 dark:bg-black/60"
          onClick={onClose}
        />
      )}
      <div
        ref={ref}
        role="dialog"
        aria-modal={phone || undefined}
        aria-labelledby={titleId}
        tabIndex={-1}
        style={
          phone
            ? undefined
            : {
                left: pos?.left,
                top: pos?.top,
                width,
                // Hidden until measured, so the first paint doesn't jump.
                visibility: pos ? undefined : 'hidden',
              }
        }
        className={clsx(
          'v2-fade-in pointer-events-auto fixed overflow-y-auto border border-v2-line bg-v2-surface outline-none',
          phone
            ? 'inset-x-0 bottom-0 max-h-[85vh] rounded-t-v2-lg pb-[env(safe-area-inset-bottom)] shadow-v2-dialog'
            : 'max-h-[min(600px,calc(100vh-24px))] rounded-v2-lg shadow-v2-float',
        )}
      >
        <header className="flex items-center justify-between gap-4 px-4 pb-1.5 pt-3">
          <h2 id={titleId} className="text-v2-14 font-semibold">
            {title}
          </h2>
          <IconButton
            size="sm"
            label="Close"
            onClick={onClose}
            className="-mr-1.5"
          >
            <IconClose />
          </IconButton>
        </header>
        <div className="px-4 pb-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-v2-line px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};

const enabledItems = (root: HTMLElement) =>
  Array.from(
    root.querySelectorAll<HTMLElement>(
      ['menuitem', 'menuitemradio']
        .map(
          (role) =>
            `[role="${role}"]:not([disabled]):not([aria-disabled="true"])`,
        )
        .join(','),
    ),
  );

/**
 * Minimal anchored menu. Closes on outside click / Escape (topmost layer
 * only); arrow keys move between enabled items; focus returns to the anchor
 * on close.
 */
export const Menu: React.FC<{
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  align?: 'left' | 'right';
  width?: number;
  className?: string;
}> = ({
  open,
  onClose,
  anchorRef,
  children,
  align = 'left',
  width = 220,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEscape(open, onClose);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, anchorRef]);
  useEffect(() => {
    if (!open || !ref.current) return;
    const anchor = anchorRef.current;
    enabledItems(ref.current)[0]?.focus();
    return () => {
      // The menu's DOM is already gone here, so focus that was inside it has
      // fallen back to <body>. Only then return it to the anchor; if the user
      // moved focus elsewhere, leave it there.
      const active = document.activeElement;
      if (!active || active === document.body) anchor?.focus();
    };
  }, [open, anchorRef]);
  if (!open) return null;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = enabledItems(ref.current!);
    if (items.length === 0) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    const n = e.key === 'ArrowDown' ? i + 1 : i < 0 ? -1 : i - 1;
    items[(n + items.length) % items.length]?.focus();
  };
  return (
    <div
      ref={ref}
      role="menu"
      onKeyDown={onKeyDown}
      style={{ width }}
      className={clsx(
        'v2-fade-in absolute z-50 mt-1 rounded-v2-lg border border-v2-line bg-v2-surface p-1 shadow-v2-float',
        align === 'right' ? 'right-0' : 'left-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const MenuItem: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    /**
     * Makes the item one choice in a single-select menu (menuitemradio).
     * Leave undefined for plain actions.
     */
    selected?: boolean;
    leading?: React.ReactNode;
    description?: string;
    tone?: 'default' | 'danger';
  }
> = ({
  selected,
  leading,
  description,
  tone = 'default',
  className,
  children,
  ...rest
}) => (
  <button
    role={selected === undefined ? 'menuitem' : 'menuitemradio'}
    type="button"
    aria-checked={selected === undefined ? undefined : selected}
    className={clsx(
      'flex w-full items-start gap-2 rounded-v2 px-2 py-1.5 text-left text-v2-13 outline-none transition-colors',
      'hover:bg-v2-sunken focus-visible:bg-v2-sunken',
      tone === 'danger' ? 'text-v2-danger' : 'text-v2-fg',
      selected && 'bg-v2-accent/[0.07]',
      className,
    )}
    {...rest}
  >
    {leading && <span className="mt-0.5 shrink-0 text-v2-fg-3">{leading}</span>}
    <span className="min-w-0 flex-1">
      <span className="block truncate">{children}</span>
      {description && (
        <span className="block text-v2-12 text-v2-fg-3">{description}</span>
      )}
    </span>
  </button>
);

export const MenuSeparator: React.FC = () => (
  <div role="separator" className="my-1 h-px bg-v2-line" />
);

export const MenuLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="px-2 pb-1 pt-1.5 text-v2-11 font-medium text-v2-fg-3">
    {children}
  </div>
);

/**
 * The ··· menu at the end of a row: Edit… and Delete…, plus anything the row
 * adds in between. Clicks and keys are kept inside the menu so a row with
 * `onActivate` does not also open its record.
 */
export const RowMenu: React.FC<{
  /** Names the row, e.g. `Actions for Thanksgiving`. */
  label: string;
  onEdit: () => void;
  onDelete: () => void;
  editLabel?: string;
  deleteLabel?: string;
  /** Extra items between Edit and Delete; call `close` after acting. */
  extra?: (close: () => void) => React.ReactNode;
  /** Disables Delete and shows why beneath it. */
  deleteDisabledReason?: string | null;
}> = ({
  label,
  onEdit,
  onDelete,
  editLabel = 'Edit…',
  deleteLabel = 'Delete…',
  extra,
  deleteDisabledReason,
}) => {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const close = () => setOpen(false);
  return (
    <span
      className="relative inline-block"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <IconButton
        ref={anchor}
        size="sm"
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <IconMore />
      </IconButton>
      <Menu
        open={open}
        onClose={close}
        anchorRef={anchor}
        align="right"
        width={deleteDisabledReason ? 240 : 180}
      >
        <MenuItem
          onClick={() => {
            close();
            onEdit();
          }}
        >
          {editLabel}
        </MenuItem>
        {extra?.(close)}
        {deleteDisabledReason ? (
          <MenuItem
            disabled
            aria-disabled="true"
            description={deleteDisabledReason}
            className="cursor-not-allowed !text-v2-fg-3 hover:bg-transparent"
          >
            {deleteLabel}
          </MenuItem>
        ) : (
          <MenuItem
            tone="danger"
            onClick={() => {
              close();
              onDelete();
            }}
          >
            {deleteLabel}
          </MenuItem>
        )}
      </Menu>
    </span>
  );
};
